import {
  Box,
  Button,
  Flex,
  HStack,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue,
  VStack,
} from '@elkaso-app/web-design';
import { masterProductFamiliesEndpoint } from 'apis/master-product-families';
import type { MasterProduct } from 'apis/master-products';
import {
  EMPTY_UOM_OPTIONS,
  masterProductsEndpoint,
  useBulkUpsertMasterProductsApi,
  useGetUomOptionsApi,
  useUpdateMasterProductApi,
} from 'apis/master-products';
import { dictionaryValues, useGetAttributeDictionaryApi } from 'apis/product-attributes';
import { EMPTY_BRAND_OPTIONS, useGetBrandOptionsApi } from 'apis/product-brands';
import { toCanonicalValue } from 'pages/exclusive-deals/utils/canonical-options';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Prompt } from 'react-router-dom';
import { variantLabel } from '../packaging';
import { AttributesDrawer } from './attributes-drawer';
import { DeleteVariantModal } from './delete-variant-modal';
import { MergeVariantModal } from './merge-variant-modal';
import { useEditableVariantsRows } from './use-editable-variants-rows';
import { VariantCard } from './variant-card';
import { sortHighlightedFirst } from './variant-highlight-sort';
import { EditableVariantRowView } from './variant-row';
import { sortVariantsByField, VariantSort, VariantSortKey } from './variant-sort';

interface Props {
  familyId: number;
  familyCategoryId?: number;
  variants: MasterProduct[];
  onDone?: () => void; // legacy hook for the old VariantsModal's read/edit mode toggle
  onDirtyChange?: (dirty: boolean) => void;
  // One-time post-merge highlight (see family-workspace-screen.tsx): ids that
  // should sort first and render highlighted. Stable reference from the
  // screen — only changes when the highlight itself changes (set on the
  // post-merge navigation, cleared on dismiss or when familyId changes).
  highlightedIds?: number[];
  highlightSourceName?: string;
  onClearHighlight?: () => void;
}

const HEADERS: { key: string; node: React.ReactNode }[] = [
  { key: 'brand', node: 'Brand' },
  { key: 'origin', node: 'Origin' },
  { key: 'state', node: 'State' },
  { key: 'packaging', node: 'Packaging' },
  { key: 'spec', node: 'Spec' },
  { key: 'attributes', node: 'Attributes' },
  { key: 'active', node: 'Active' },
  { key: 'actions', node: '' },
];

export const EditableVariantsGrid = ({
  familyId,
  familyCategoryId,
  variants,
  onDone,
  onDirtyChange,
  highlightedIds,
  highlightSourceName,
  onClearHighlight,
}: Props) => {
  const queryClient = useQueryClient();
  // Below md the rows render as stacked editable cards instead of the
  // 8-column table — a mount branch, so only one layout is mounted at a time
  // (same data, hooks and handlers feed both).
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Curated "top product" star flag: kept OUTSIDE the bulk-upsert diffing machinery
  // (useEditableVariantsRows' rows/original) — it's a grid-local map keyed by saved
  // variant id, seeded from the `variants` prop and toggled optimistically via its own
  // PUT, independent of Save/dirty/buildPayload.
  const [topFlags, setTopFlags] = useState<Record<number, boolean>>({});
  // Ids with a toggle currently in flight. Serves two purposes: (1) disables that row's
  // star button so a same-row double-click can't fire a second concurrent PUT — which in
  // turn keeps the onError rollback below safe, since there's never a second in-flight
  // request that could resolve out of order; (2) tells the seeding effect below which ids
  // to leave alone. Mirrored into a ref (not read directly in the effect) so the effect
  // itself only depends on `variants` and doesn't re-seed merely because a toggle
  // started/settled.
  const [pendingTopFlagIds, setPendingTopFlagIds] = useState<Record<number, boolean>>({});
  const pendingTopFlagIdsRef = useRef(pendingTopFlagIds);
  useEffect(() => {
    pendingTopFlagIdsRef.current = pendingTopFlagIds;
  }, [pendingTopFlagIds]);
  useEffect(() => {
    // MERGE, not replace: `useUpdateMasterProductApi`'s onSuccess invalidates the same
    // query key this grid's variants come from, so toggling row A (or saving unrelated
    // edits) can refetch mid-flight for row B's toggle. A wholesale replace here would
    // stomp row B's still-in-flight optimistic value with a stale server snapshot
    // (visible flicker to the wrong star state). Ids with a pending toggle keep their
    // current optimistic value; every other id takes the fresh server value.
    setTopFlags((prev) => {
      const next: Record<number, boolean> = {};
      variants.forEach((v) => {
        if (v.id == null) return;
        next[v.id] = pendingTopFlagIdsRef.current[v.id] ? prev[v.id] : !!v.isTopProduct;
      });
      return next;
    });
  }, [variants]);

  const { mutate: updateVariantFlag } = useUpdateMasterProductApi();
  // Optimistic star toggle, independent of the bulk-save machinery: flips the local map
  // immediately, rolls back on error. The hook's own onSuccess invalidation re-seeds the
  // map from fresh data via the effect above. Guarded by pendingTopFlagIds — both here
  // (belt-and-braces) and via the disabled button in variant-row.tsx — so a row can only
  // ever have one toggle in flight, which is what makes the unconditional `!next`
  // rollback in onError safe (no second, later-resolving request to race against).
  const toggleTopProduct = (id: number) => {
    if (pendingTopFlagIds[id]) return;
    const next = !topFlags[id];
    setTopFlags((prev) => ({ ...prev, [id]: next }));
    setPendingTopFlagIds((prev) => ({ ...prev, [id]: true }));
    updateVariantFlag(
      { id, data: { isTopProduct: next } },
      {
        onError: () => setTopFlags((prev) => ({ ...prev, [id]: !next })),
        onSettled: () =>
          setPendingTopFlagIds((prev) => {
            const rest = { ...prev };
            delete rest[id];
            return rest;
          }),
      }
    );
  };

  const { data: dictionaryData } = useGetAttributeDictionaryApi({ catalogCategoryId: familyCategoryId });
  const dictionary = dictionaryData?.data;
  const { data: uomData } = useGetUomOptionsApi();
  const uomOptions = uomData ?? EMPTY_UOM_OPTIONS;
  const { data: brandData } = useGetBrandOptionsApi();
  const brandOptions = brandData ?? EMPTY_BRAND_OPTIONS;
  // Memoized (not inline): dictionaryValues returns a fresh array each call, and these feed
  // canonicalVariants below. An inline call here made canonicalVariants recompute every render
  // (new array identity even though `dictionary` itself was unchanged), which re-fired the
  // reset effect below on every render — reset() mints fresh clientIds, remounting every row
  // (losing expander state) and re-rendering in a loop. Keyed on [dictionary] so it only
  // recomputes when the dictionary itself actually changes.
  const stateCanon = useMemo(() => dictionaryValues(dictionary, 'state'), [dictionary]);
  const originCanon = useMemo(() => dictionaryValues(dictionary, 'origin'), [dictionary]);

  // Canonicalize state/origin/brand/baseUnit at load time so the pristine snapshot and working
  // rows both hold the canonical value: untouched rows stay "unchanged" (not sent), and rows
  // dirtied via any other field submit enum-valid/canonical values instead of raw legacy casing
  // (e.g. "Frozen" -> 'frozen', "italy" -> 'Italy'). Mirrors VariantFormModal's fromProduct
  // mapping. State/origin canon comes from the (category-scoped) attribute dictionary; brand
  // canon from the product-brands dictionary (case-insensitive match, like the rest).
  const canonicalVariants = useMemo(
    () =>
      variants.map((v) => ({
        ...v,
        state: toCanonicalValue(stateCanon, v.state ?? ''),
        origin: toCanonicalValue(originCanon, v.origin ?? ''),
        brand: toCanonicalValue(brandOptions, v.brand ?? ''),
        baseUnit: toCanonicalValue(uomOptions, v.baseUnit ?? ''),
        itemUnit: toCanonicalValue(uomOptions, v.itemUnit ?? '') || null,
        packUnit: toCanonicalValue(uomOptions, v.packUnit ?? '') || null,
      })),
    [variants, stateCanon, originCanon, brandOptions, uomOptions]
  );

  // Post-merge highlight: front-sort the highlighted ids for the *seed* the
  // grid uses (initial mount + any genuine reseed below) — never touches the
  // row model itself, so it can't affect dirty/validation. Kept as a separate
  // memo from canonicalVariants (rather than folded into it) so the reset
  // effect's signature — computed from canonicalVariants — stays unaffected
  // by highlightedIds alone: dismissing the highlight (which clears
  // highlightedIds) must not force an immediate reseed of already-rendered
  // rows, only stop applying the sort to the *next* genuine reseed.
  // Invariant: the initial seed order reaches useEditableVariantsRows via its
  // lazy useState initializer, so highlightedIds must be resolved BEFORE this
  // grid first mounts — the screen guarantees that (highlight state is set from
  // navigation state on the screen's first commit, while the grid mounts later,
  // gated on the family fetch). Don't lift that gate without revisiting this.
  const highlightedIdSet = useMemo(() => new Set(highlightedIds ?? []), [highlightedIds]);
  // User sort (Brand/Origin header buttons). Applied to the SEED like the highlight
  // sort — never to the live row model — because reordering rows means reseeding, and
  // reset() mints fresh clientIds (discarding edits). The header buttons are therefore
  // disabled while dirty, and the sort-change effect below guards on dirty as well.
  // Local state, not a URL param: this grid is not a URL-driven list table, and the
  // screen remounts it per family (key={family.id}), which is the right reset scope.
  const [sort, setSort] = useState<VariantSort>(null);
  const seededVariants = useMemo(
    () => sortHighlightedFirst(sortVariantsByField(canonicalVariants, sort), (v) => v.id, highlightedIdSet),
    [canonicalVariants, highlightedIdSet, sort]
  );

  const {
    rows,
    errors,
    errorsByClientId,
    packagingDirtyClientIds,
    dirty,
    updateRow,
    insert,
    remove,
    removeSavedRow,
    duplicate,
    appendFrom,
    reset,
    buildPayload,
  } = useEditableVariantsRows(seededVariants);

  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  // Unsaved-edit protection: in-app navigation (react-router v5 Prompt) and hard
  // refresh/tab-close (beforeunload) both get a confirmation while dirty.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // Explicit reset replaces the old remount-on-refetch hack. Background
  // refetches (e.g. another tab's edit, a stale cache revalidation) must never
  // silently discard in-progress edits, so re-seeding only happens when the
  // grid is clean — except right after our own successful save, where the
  // refetch is expected to bring back the same data plus server-assigned ids,
  // and skipping the reset would leave new rows without an id, re-POSTing
  // them as duplicates on the next save.
  const justSavedRef = useRef(false);
  const isFirstRenderRef = useRef(true);
  // Belt-and-braces guard, independent of canonicalVariants' identity being stable: tracks the
  // field-level content of the last data this effect actually seeded, so a future unstable
  // dependency (new array identity, same content) can cause at most one wasted reset instead of
  // a render loop — reset() only runs when the incoming data is genuinely different by id/fields.
  const lastSeededSignatureRef = useRef<string | null>(null);
  // Covers the save→refetch round-trip: once a save succeeds, justSavedRef flips true but rows
  // aren't reset until the post-save refetch lands below — in that window `dirty` is still true
  // and the mutation is no longer loading, so Save would otherwise be clickable and buildPayload()
  // would re-POST the still id-less new rows as duplicates. isSettling keeps Save disabled for
  // that window and is cleared as soon as the effect below actually re-seeds rows (for any reason
  // while it's set, not just the justSaved path).
  const [isSettling, setIsSettling] = useState(false);
  useEffect(() => {
    const signature = JSON.stringify(canonicalVariants);
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      lastSeededSignatureRef.current = signature;
      return;
    }
    if (dirty && !justSavedRef.current) return;
    // Clear save-settling flags early if we just saved, even if incoming data is unchanged.
    // This ensures that if the post-save refetch returns data byte-identical to the last
    // seeded snapshot (e.g. BE normalized a field back), isSettling still clears and Save
    // becomes clickable again. Stale justSavedRef would otherwise block the dirty guard on
    // the next background refetch.
    if (justSavedRef.current) {
      justSavedRef.current = false;
      setIsSettling(false);
    }
    if (signature === lastSeededSignatureRef.current) return;
    reset(seededVariants);
    lastSeededSignatureRef.current = signature;
    // `dirty`/`reset` intentionally excluded: this effect must only re-run when
    // fresh data arrives (`canonicalVariants` identity), not on every dirty flip —
    // otherwise typing a single character would immediately re-trigger it. No
    // react-hooks eslint plugin is configured in this repo (see
    // inline-variant-form.tsx), so no disable directive is needed here.
  }, [canonicalVariants]);

  // Applying a sort re-seeds the grid immediately. Separate from the data-driven reset
  // effect above on purpose: that one is keyed on fresh data (canonicalVariants identity)
  // and tracks lastSeededSignatureRef, while this one fires on a pure view reorder of the
  // SAME data — updating the signature ref here would wrongly suppress the next genuine
  // data reseed. Only reachable when clean (the header buttons are disabled while dirty),
  // but guarded anyway since reset() discards edits. First render is skipped: the initial
  // seed already reaches useEditableVariantsRows via its lazy initializer.
  const isFirstSortRef = useRef(true);
  useEffect(() => {
    if (isFirstSortRef.current) {
      isFirstSortRef.current = false;
      return;
    }
    if (dirty) return;
    reset(seededVariants);
    // `seededVariants`/`dirty`/`reset` intentionally excluded — this must fire only when
    // the sort itself changes, mirroring the data effect's deps convention above.
  }, [sort]);

  const cycleSort = (key: VariantSortKey) => {
    if (dirty) return;
    setSort((prev) => (prev?.key !== key ? { key, dir: 'asc' } : prev.dir === 'asc' ? { key, dir: 'desc' } : null));
  };

  const { mutate: bulkUpsert, isLoading, error: saveError } = useBulkUpsertMasterProductsApi();

  const serverErrors = saveError?.response?.data?.errors ?? [];

  // Attributes drawer: one instance for the whole grid, targeting whichever
  // row's chips cell was clicked (rather than mounting a drawer per row).
  const [attributesDrawerFor, setAttributesDrawerFor] = useState<{ clientId: string } | null>(null);
  const attributesDrawerRowIndex = attributesDrawerFor
    ? rows.findIndex((r) => r.clientId === attributesDrawerFor.clientId)
    : -1;
  const attributesDrawerRow = attributesDrawerRowIndex >= 0 ? rows[attributesDrawerRowIndex] : null;
  const attributesDrawerLabel = attributesDrawerRow
    ? attributesDrawerRow.brand || attributesDrawerRow.spec || `Row ${attributesDrawerRowIndex + 1}`
    : '';

  // Delete modal: one grid-owned instance targeting whichever saved row's trash was
  // clicked, mirroring the attributes drawer above. Unlike the attributes drawer,
  // this snapshots {clientId, id, label} into state AT CLICK TIME rather than
  // re-deriving it from `rows` on every render — a background reseed while the
  // DELETE is in flight mints fresh clientIds for every row, so a `rows.findIndex`
  // lookup here would go stale (returning -1) and silently close the confirmation
  // dialog for an irreversible action out from under the user.
  const [deleteTarget, setDeleteTarget] = useState<{ clientId: string; id: number; label: string } | null>(null);

  // Same snapshot-at-click-time reasoning as deleteTarget above: a background reseed
  // while the merge is in flight mints fresh clientIds, so a rows.findIndex lookup here
  // would go stale and close the dialog out from under the user.
  const [mergeTarget, setMergeTarget] = useState<{ clientId: string; id: number; label: string } | null>(null);

  const onSave = () => {
    if (errors.length > 0) return;
    const changedRows = buildPayload();
    if (changedRows.length === 0) {
      onDone?.();
      return;
    }
    bulkUpsert(
      { familyId, rows: changedRows },
      {
        onSuccess: () => {
          justSavedRef.current = true;
          setIsSettling(true);
          onDone?.();
        },
      }
    );
  };

  const onCancel = () => {
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    reset(seededVariants);
    onDone?.();
  };

  const issueCount = errors.length;
  const saveLabel = issueCount > 0 ? `Save (${issueCount} issue${issueCount === 1 ? '' : 's'})` : 'Save';

  const labelForRow = (index: number): { clientId: string; id: number; label: string; origin: string } | null => {
    const target = rows[index];
    // Both callers are wired only to saved rows (variant-row.tsx's canRemove===false
    // branch), so target.id is always defined — this is a type-narrowing belt.
    if (!target || target.id === undefined) return null;
    const label =
      variantLabel({
        id: target.id,
        brand: target.brand,
        spec: target.spec,
        baseUnit: target.baseUnit,
        itemUnit: target.itemUnit,
        netContent: target.netContent.trim() ? Number(target.netContent) : null,
        packUnit: target.packUnit,
        packSize: target.packSize.trim() ? Number(target.packSize) : null,
      }) || `Row ${index + 1}`;
    return { clientId: target.clientId, id: target.id, label, origin: target.origin.trim() };
  };

  // Every OTHER saved variant, for the merge modal's counterpart picker. Unsaved rows
  // have no id to merge into, so they are excluded. Memoized (not inline) so this
  // component — which stays mounted while the merge mutation is in flight — doesn't
  // mint a fresh array identity on every render; keyed on the values it actually
  // derives from, not on `rows` alone, since mergeTarget?.id also affects the filter.
  const savedRowCount = rows.filter((row) => row.id !== undefined).length;
  const mergeSiblings = useMemo(
    () =>
      rows
        .map((_row, index) => labelForRow(index))
        .filter((entry): entry is NonNullable<ReturnType<typeof labelForRow>> => !!entry)
        .filter((entry) => entry.id !== mergeTarget?.id)
        // Origin is appended for the picker only — variantLabel deliberately excludes it
        // (brand · spec · packaging), but when a family's variants differ mainly by origin
        // the dropdown options would otherwise be indistinguishable.
        .map((entry) => ({ id: entry.id, label: entry.origin ? `${entry.label} · ${entry.origin}` : entry.label })),
    [rows, mergeTarget?.id]
  );

  // One props bag for a row, shared by both layouts: the desktop table row and
  // the mobile card receive EXACTLY the same props/handlers, so behavior
  // (dirty tracking, validation, expander, actions) cannot drift between them.
  const rowViewProps = (row: (typeof rows)[number], index: number) => ({
    row,
    index,
    messages: errorsByClientId.get(row.clientId) ?? [],
    isPackagingDirty: packagingDirtyClientIds.has(row.clientId),
    columnCount: HEADERS.length,
    isLastRow: index === rows.length - 1,
    dictionary,
    uomOptions,
    brandOptions,
    onChange: updateRow,
    onInsert: insert,
    onRemove: remove,
    onDeleteVariant: (targetIndex: number) => {
      const target = labelForRow(targetIndex);
      if (target) setDeleteTarget(target);
    },
    onMergeVariant: (targetIndex: number) => {
      const target = labelForRow(targetIndex);
      if (target) setMergeTarget(target);
    },
    canMergeVariant: savedRowCount > 1,
    onDuplicate: duplicate,
    onEnterAppend: appendFrom,
    onOpenAttributesDrawer: (clientId: string) => setAttributesDrawerFor({ clientId }),
    isHighlighted: row.id !== undefined && highlightedIdSet.has(row.id),
    highlightSourceName,
    isTopProduct: row.id != null ? !!topFlags[row.id] : false,
    isTopProductPending: row.id != null ? !!pendingTopFlagIds[row.id] : false,
    onToggleTopProduct: toggleTopProduct,
  });

  return (
    <VStack align='stretch' spacing='md'>
      <Prompt when={dirty} message='Discard unsaved variant changes?' />

      {!!highlightedIds?.length && (
        <HStack justify='space-between' bg='yellow.50' borderRadius='md' py='xs' px='sm'>
          <Text fontSize='sm' color='gray.700'>
            Merged rows highlighted
          </Text>
          <Button size='xs' variant='link' onClick={onClearHighlight}>
            Clear
          </Button>
        </HStack>
      )}

      <HStack justify='flex-end' py='sm' px='xs'>
        <Button size='sm' variant='outline' onClick={appendFrom}>
          Add row
        </Button>
      </HStack>

      {serverErrors.length > 0 && (
        <Box bg='red.50' borderRadius='md' p='sm'>
          <Text fontSize='sm' color='red.600' fontWeight='semibold'>
            Save failed — fix these and try again:
          </Text>
          {serverErrors.map((msg, i) => (
            <Text key={i} fontSize='sm' color='red.600'>
              {msg}
            </Text>
          ))}
        </Box>
      )}

      {isMobile ? (
        <VStack align='stretch' spacing='10px'>
          {rows.map((row, index) => (
            <VariantCard key={row.clientId} {...rowViewProps(row, index)} />
          ))}
        </VStack>
      ) : (
        <Box overflowX='auto' border='1px solid' borderColor='gray.200' borderRadius='md'>
          <Table
            size='sm'
            w='100%'
            sx={{
              'th, td': { borderWidth: '1px', borderColor: 'gray.200', px: '1.5', py: '1.5', verticalAlign: 'middle' },
              th: {
                bg: 'white',
                textTransform: 'none',
                fontSize: 'xs',
                letterSpacing: 'normal',
                position: 'sticky',
                top: 0,
                zIndex: 1,
                whiteSpace: 'nowrap',
              },
              'tbody tr:hover td': { bg: 'blackAlpha.50' },
            }}>
            <Thead>
              <Tr>
                {HEADERS.map((h) =>
                  h.key === 'brand' || h.key === 'origin' ? (
                    <Th key={h.key}>
                      <Tooltip
                        label={dirty ? 'Save or discard changes to sort' : `Sort by ${h.node}`}
                        shouldWrapChildren>
                        <Button
                          size='xs'
                          variant='ghost'
                          fontSize='xs'
                          textTransform='inherit'
                          letterSpacing='inherit'
                          fontWeight='inherit'
                          px='1'
                          isDisabled={dirty}
                          onClick={() => cycleSort(h.key as VariantSortKey)}
                          aria-label={`sort-by-${h.key}`}>
                          {h.node}
                          {sort?.key === h.key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                        </Button>
                      </Tooltip>
                    </Th>
                  ) : (
                    <Th key={h.key}>{h.node}</Th>
                  )
                )}
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row, index) => (
                <EditableVariantRowView key={row.clientId} {...rowViewProps(row, index)} />
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <AttributesDrawer
        isOpen={!!attributesDrawerRow}
        onClose={() => setAttributesDrawerFor(null)}
        rowLabel={attributesDrawerLabel}
        attributes={attributesDrawerRow?.attributes ?? {}}
        dictionary={dictionary}
        onChange={(attributes) => {
          if (attributesDrawerRow) updateRow(attributesDrawerRow.clientId, { attributes });
        }}
      />

      <DeleteVariantModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        variantId={deleteTarget?.id}
        variantLabel={deleteTarget?.label ?? ''}
        onDeleted={() => {
          // Drop the row locally (from BOTH rows and original, by clientId — not a
          // captured index, which a background reseed while the DELETE was in flight
          // could have made stale/wrong) as well as invalidating: the reseed effect
          // only calls reset() when !dirty || justSaved, so a refetch alone leaves the
          // deleted row on screen in a dirty grid, and removing only from `rows` would
          // leave `original` one row longer forever, pinning `dirty` true for good.
          if (deleteTarget) removeSavedRow(deleteTarget.clientId);
          // The BE writes the 'deleted' activity entry transactionally, but nothing
          // invalidates the panel's query, so without this the entry only appears
          // after a reload. Two-element prefix matches every page of the feed.
          queryClient.invalidateQueries([`${masterProductFamiliesEndpoint}/activity`, familyId]);
          setDeleteTarget(null);
        }}
      />

      <MergeVariantModal
        isOpen={!!mergeTarget}
        onClose={() => setMergeTarget(null)}
        variantId={mergeTarget?.id}
        variantLabel={mergeTarget?.label ?? ''}
        siblings={mergeSiblings}
        onMerged={({ loserId }) => {
          // The loser is gone server-side. Drop it from BOTH rows and original by
          // clientId — same reasoning as the delete path: removing from `rows` alone
          // would leave `original` one row longer forever, pinning `dirty` true. The
          // reseed effect alone isn't enough — it bails while the grid has unsaved
          // edits (`dirty && !justSavedRef.current`), so a dirty grid would otherwise
          // keep rendering and editing the deleted row until reload.
          //
          // The loser isn't necessarily mergeTarget (the clicked row): after a Swap the
          // clicked row survives and the counterpart is the one deleted. Look the loser
          // up in the live `rows` by server id — read at callback time, not captured
          // earlier, so it reflects whichever variant the merge actually removed.
          const loserRow = rows.find((row) => row.id === loserId);
          if (loserRow) removeSavedRow(loserRow.clientId);
          queryClient.invalidateQueries([masterProductsEndpoint]);
          queryClient.invalidateQueries([`${masterProductFamiliesEndpoint}/activity`, familyId]);
          setMergeTarget(null);
        }}
      />

      <Flex
        position='sticky'
        bottom='0'
        bg='white'
        borderTopWidth='1px'
        borderColor='gray.200'
        justify='flex-end'
        py='sm'
        px='xs'
        gap='sm'
        zIndex={2}>
        <Button size='sm' variant='ghost' colorScheme='gray' onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size='sm'
          colorScheme='red'
          isLoading={isLoading || isSettling}
          isDisabled={issueCount > 0 || !dirty || isSettling}
          onClick={onSave}>
          {saveLabel}
        </Button>
      </Flex>
    </VStack>
  );
};
